import {
  Dialog as ChakraDialog,
  Portal,
  type PortalProps,
} from '@chakra-ui/react';
import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEventHandler,
  type PointerEventHandler,
} from 'react';
import { CloseIcon } from './closeIcon';
import { pressWash } from './press';

const defaultPortalled =
  typeof process === 'undefined' || process.env.NODE_ENV !== 'test';

export const DialogRoot = ChakraDialog.Root;
export const DialogHeader = ChakraDialog.Header;
export const DialogBody = ChakraDialog.Body;

export interface DialogBlurLayerProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual styles for the dim/blur, applied to the absolute child. */
  layerStyle?: CSSProperties;
  portalled?: boolean;
  portalRef?: PortalProps['container'];
}

// Static, non-animated full-viewport overlay layer carrying the dim and blur.
//
// iOS Safari 26 (Liquid Glass) samples background-color and backdrop-filter
// on fixed elements near the viewport edges to tint its status bar/toolbar,
// and that chrome re-tint lags behind the page — appearing as the overlay
// "lagging" at the top/bottom on open and close. The fixed parent therefore
// stays visually empty, and the dim/blur live on a position:absolute child,
// which the tinting sampler ignores entirely.
export const DialogBlurLayer = forwardRef<HTMLDivElement, DialogBlurLayerProps>(
  function DialogBlurLayer(props, ref) {
    // Caller style must merge over the base styles (never replace them) or
    // the layer loses its fixed positioning and becomes invisible.
    const {
      portalled = defaultPortalled,
      portalRef,
      style,
      layerStyle,
      ...rest
    } = props;
    return (
      <Portal disabled={!portalled} container={portalRef}>
        <div
          ref={ref}
          {...rest}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1408,
            pointerEvents: 'none',
            ...style,
          }}
        >
          <div
            aria-hidden="true"
            data-testid="modal-blur-layer-visual"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              ...layerStyle,
            }}
          />
        </div>
      </Portal>
    );
  },
);

export interface DialogContentProps extends ChakraDialog.ContentProps {
  portalled?: boolean;
  portalRef?: PortalProps['container'];
  onPositionerClick?: MouseEventHandler<HTMLDivElement>;
  onPositionerPointerDown?: PointerEventHandler<HTMLDivElement>;
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent(props, ref) {
    const {
      children,
      portalled = defaultPortalled,
      portalRef,
      onPositionerClick,
      onPositionerPointerDown,
      ...rest
    } = props;
    return (
      <Portal disabled={!portalled} container={portalRef}>
        <ChakraDialog.Positioner
          onClick={onPositionerClick}
          onPointerDown={onPositionerPointerDown}
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1410}
          w="100vw"
          h="100dvh"
        >
          <ChakraDialog.Content
            ref={ref}
            zIndex={1411}
            style={{ zIndex: 1411 }}
            {...rest}
          >
            {children}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    );
  },
);

export const DialogCloseTrigger = forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>(function DialogCloseTrigger(props, ref) {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="2"
      right="2"
      // User-requested larger X; retain the audited 44px header/tap slot.
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="44px"
      h="44px"
      css={pressWash}
      ref={ref}
      aria-label="Close"
      {...props}
    >
      {props.children || <CloseIcon />}
    </ChakraDialog.CloseTrigger>
  );
});
